/**
 * Flexible / Confirmed affordance for schedule blocks — the existing
 * 🔓/🔒 iconography from the review timeline, reused verbatim so the two
 * screens speak one visual language (constitution ratified decision #2).
 *
 * Two sizes of the same statement:
 *   - <CommitmentGlyph>  icon-only, for tight pills (month cells, week and
 *                        day blocks)
 *   - <CommitmentChip>   icon + label, where there is room (agenda rows,
 *                        the edit modal header)
 *
 * Both render nothing when the commitment is unknown, and neither counts,
 * chases, or prompts (SC-6, zero-nag) — they only say what a block is.
 * Copy and border/fill styling live in ./commitmentPresentation.
 */

import React from 'react'
import { Lock, Unlock } from 'lucide-react'

import type { CommitmentState } from '../../services/scheduleService'
import { COMMITMENT_HINT, COMMITMENT_LABEL } from './commitmentPresentation'

interface CommitmentGlyphProps {
  state: CommitmentState | null | undefined
  /** Tailwind size classes; defaults to the pill size. */
  className?: string
}

export const CommitmentGlyph: React.FC<CommitmentGlyphProps> = ({
  state,
  className = 'w-3 h-3',
}) => {
  if (!state) return null
  const Icon = state === 'flexible' ? Unlock : Lock
  return (
    <Icon
      className={`${className} flex-shrink-0 opacity-80`}
      aria-label={COMMITMENT_LABEL[state]}
      role="img"
    />
  )
}

interface CommitmentChipProps {
  state: CommitmentState | null | undefined
}

export const CommitmentChip: React.FC<CommitmentChipProps> = ({ state }) => {
  if (!state) return null
  const flexible = state === 'flexible'
  return (
    <span
      className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-app-micro font-medium ${
        flexible
          ? 'bg-brand-subtle text-brand border border-dashed border-brand-subtle-border'
          : 'bg-ok/10 text-ok border border-ok/30'
      }`}
      title={COMMITMENT_HINT[state]}
    >
      <CommitmentGlyph state={state} className="w-3 h-3" />
      {COMMITMENT_LABEL[state]}
    </span>
  )
}

export default CommitmentChip
